import * as React from 'react';
import * as _ from 'lodash';
import {
  Certificate,
  ControlState,
  ControlStyleClass,
  ControlValidation,
  DropZone,
  DropZoneProps,
  FileType,
  Footer,
  Logging,
  renderEdit,
} from '@vcd-ecm-ui/Common';
import { VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import AdditionalDocumentationProps from './AdditionalDocumentationProps.interface';
import '../steps.scss';
import SelectedItems from '../../Common/selectedItems';

const BYTES_PER_MEGABYTE = 1048576;
const BYTES_PER_GIGABYTE = BYTES_PER_MEGABYTE * 1024;

const controlStyle: ControlStyleClass = null;

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlValidation: ControlValidation = {
  requiredFields: [],
  customMessages: [],
};

const AdditionalDocumentation = ({
  gatewayEndPoint,
  step,
  certificate,
  selectedItems,
  onCancel,
  onContinuteSave,
  dropFiles,
  setDropFiles,
  statusMap,
  setStatusMap,
  saveInProgress,
}: AdditionalDocumentationProps): JSX.Element => {
  Logging.Debug('Additional Documentation');

  const [filesError, setFilesError] = React.useState(null);

  // Continue / Save Button
  const onSaveContinue = (): void => {
    const copyCertificate: Certificate = _.cloneDeep(certificate);
    const copySelectedItems: SelectedItems = _.cloneDeep(selectedItems);

    // Do not persist the files to the Certificate.  This will bloat the Certificate size.  Files are stored seperately

    Logging.Info('Continue to Save:', copyCertificate);

    onContinuteSave(step, copyCertificate, copySelectedItems);
  };

  // Get the Invalid Files
  const getInvalidFiles = (files: File[], mapStatus: Map<string, boolean | string>): File[] => {
    const invalidFiles: File[] = [];

    files.forEach((file: File) => {
      const data: boolean | string = mapStatus.get(file.name);

      if (typeof (data) === 'string') {
        invalidFiles.push(file);
      }
    });

    return invalidFiles;
  };

  // Validate the Step
  const validateStep = (): boolean => {
    // Check for all File Errors
    if (filesError) {
      return false;
    }

    // Check for Individual File Errors
    if (getInvalidFiles(dropFiles, statusMap).length > 0) {
      return false;
    }

    return true;
  };

  // Additional Documentation Drop Zone Parameters
  const additionalDocumentationDropProps: DropZoneProps = {
    dragTextDescription: 'Drag the Additional Documentation file(s) here, or click to select files.',
    dropTextDescription: 'Drop the Additional Documentation file(s) here.',
    showRestrictions: true,
    maxFileSize: BYTES_PER_MEGABYTE * 30, // 30 MB
    maxFilesSize: null,
    maxNumberFiles: 20,
    allowedFileTypes: [
      FileType.Pdf,
      FileType.Png,
      FileType.Gif,
      FileType.Jpg,
    ],
    dragDropImage: '/images/drag-drop-zip.png',
    dropFiles,
    setDropFiles,
    statusMap,
    setStatusMap,
    setFilesError,
  };

  const canSaveContinue = !saveInProgress && validateStep();

  return (
    <>
      <div className="stepContent stepContentColumn">
        <div className="additionalDocumentationHeader">
          {
            (dropFiles.length > 0) && (
              <>
                Drag the Additional Documentation file(s) here, or click to select files.
              </>
            )
          }
        </div>
        <DropZone {...additionalDocumentationDropProps} />
        {
          filesError
          && (
            <div className="dropZoneFooter">
              <VtxIcon type="exclamation-circle" fontSize={20} color="red" className="dropZoneFooterIcons" />
              <span>{filesError}</span>
            </div>
          )
        }
      </div>
      <Footer onCancel={onCancel} saveEnabled={canSaveContinue} onSave={onSaveContinue} saveText="Save" />
    </>
  );
};

export default AdditionalDocumentation;
